import React, { useState } from 'react';
import { couponService } from '../../services/couponService';
import BaseModal from '../common/BaseModal';

const AddCouponModal = ({ isOpen, onClose, onSuccess, locationId, locationName }) => {
  const getDefaultExpiryDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() + 3);
    return date.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    wartosc: '',
    data_waznosci: getDefaultExpiryDate(),
    numer_telefonu: '',
    sposob_platnosci: 'gotowka',
    kod_zewnetrzny: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.wartosc || parseFloat(formData.wartosc) <= 0) {
      setError('Wartość kuponu musi być większa od 0');
      return;
    }

    if (!formData.data_waznosci) {
      setError('Data ważności jest wymagana');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const couponData = {
        ...formData,
        wartosc: parseFloat(formData.wartosc),
        location_id: locationId
      };

      const response = await couponService.addCoupon(couponData);

      if (response.success || response.data) {
        setSuccess(`✅ Kupon dodany pomyślnie! Kod: ${response.data?.kod || 'Wygenerowany'}`);

        // Reset formularza
        setFormData({
          wartosc: '',
          data_waznosci: getDefaultExpiryDate(),
          numer_telefonu: '',
          sposob_platnosci: 'gotowka',
          kod_zewnetrzny: ''
        });

        // Callback sukcesu
        setTimeout(() => {
          onSuccess && onSuccess(response.data);
          setSuccess('');
        }, 1500);
      } else {
        setError(response.message || 'Błąd podczas dodawania kuponu');
      }
    } catch (err) {
      // error_response serwera niesie komunikat w polu 'error' (nie 'message');
      // samo err.message to tylko "Request failed with status code 400".
      const opisSerwera = err.response?.data?.error || err.response?.data?.message;
      if (opisSerwera && String(opisSerwera).indexOf('w obiegu') !== -1) {
        // W4 recykling kart: na jednym kodzie zewnetrznym moze byc najwyzej
        // JEDEN aktywny kupon - karta wraca do obiegu dopiero po zuzyciu salda.
        setError(
          'Ta karta ma jeszcze aktywne saldo i jest w obiegu - nie mozna nabic '
          + 'na nia nowego kuponu. Uzyj innej karty albo poczekaj, az obecne '
          + 'saldo zostanie w calosci wykorzystane. (' + opisSerwera + ')'
        );
      } else {
        setError('Błąd podczas dodawania kuponu: ' + (opisSerwera || err.message || 'Nieznany błąd'));
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      wartosc: '',
      data_waznosci: getDefaultExpiryDate(),
      numer_telefonu: '',
      sposob_platnosci: 'gotowka',
      kod_zewnetrzny: ''
    });
    setError('');
    setSuccess('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Sprawdź czy lokalizacja jest wybrana
  if (!locationId) {
    return (
      <BaseModal
        isOpen={isOpen}
        onClose={onClose}
        title="Brak wybranej lokalizacji"
        size="sm"
        zIndex={1000}
        headerIcon={
          <div style={{
            width: '2rem',
            height: '2rem',
            background: 'transparent',
            borderRadius: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1rem'
          }}>
            ⚠️
          </div>
        }
        headerStyle={{
          border: '1px solid var(--danger)',
          color: 'var(--danger)',
        }}
      >
        <div style={{ textAlign: 'center', padding: '0.5rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📍</div>
          <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--text-secondary)' }}>
            Wybierz lokalizację
          </h4>
          <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: '0 0 1.5rem 0' }}>
            Aby dodać kupon, musisz najpierw wybrać lokalizację w menu głównym.
          </p>
          <button
            onClick={onClose}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--text-muted)', color: 'var(--text-muted)',
              fontWeight: '500',
              fontSize: '12px'
            }}
          >
            Zamknij
          </button>
        </div>
      </BaseModal>
    );
  }

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Dodaj nowy kupon"
      subtitle={locationName ? `📍 ${locationName}` : 'Tworzenie kuponu podarunkowego'}
      size="sm"
      zIndex={1000}
      headerIcon={
        <div style={{
          width: '2rem',
          height: '2rem',
          background: 'var(--bg-secondary)',
          borderRadius: '5px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1rem'
        }}>
          🎟️
        </div>
      }
      headerStyle={{
        border: '1px solid var(--info)',
        color: 'var(--info)',
      }}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            style={{
              padding: '6px 12px',
              background: 'var(--bg)',
              color: 'var(--text-muted)',
              border: '1px solid var(--border)',
              fontWeight: '500',
              fontSize: '12px'
            }}
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || !formData.wartosc || parseFloat(formData.wartosc) <= 0}
            style={{
              padding: '6px 12px',
              border: `1px solid ${loading || !formData.wartosc || parseFloat(formData.wartosc) <= 0 ? 'var(--info-light)' : 'var(--info)'}`,
              color: 'var(--info)',
              fontWeight: '600',
              fontSize: '12px',
              opacity: loading || !formData.wartosc || parseFloat(formData.wartosc) <= 0 ? 0.7 : 1
            }}
          >
            {loading ? '⏳ Dodawanie...' : '🎟️ Dodaj kupon'}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>

        {/* Sekcja wartości kuponu */}
        <div style={{
          marginBottom: '0.75rem',
          padding: '0.75rem',
          background: 'var(--bg)',
          borderRadius: '5px',
          border: '1px solid var(--border)'
        }}>
          <h6 style={{ margin: '0 0 0.75rem 0', fontWeight: '600', color: 'var(--text-secondary)', fontSize: '12px' }}>
            💰 Wartość kuponu
          </h6>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            {/* Wartość */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: '500',
                color: 'var(--text-muted)',
                marginBottom: '3px'
              }}>
                Wartość (zł) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={formData.wartosc}
                onChange={(e) => handleChange('wartosc', e.target.value)}
                required
                placeholder="np. 50.00"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: `2px solid ${formData.wartosc && parseFloat(formData.wartosc) > 0 ? 'var(--success)' : 'var(--border)'}`,
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: '600',
                  height: '36px'
                }}
              />
            </div>

            {/* Data ważności */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: '500',
                color: 'var(--text-muted)',
                marginBottom: '3px'
              }}>
                Data ważności *
              </label>
              <input
                type="date"
                value={formData.data_waznosci}
                onChange={(e) => handleChange('data_waznosci', e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  fontSize: '12px',
                  height: '36px'
                }}
              />
            </div>
          </div>

          {/* Szybkie kwoty */}
          <div style={{ marginTop: '0.75rem' }}>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px' }}>Szybki wybór wartości:</div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {[20, 50, 100, 150, 200, 500].map(amount => (
                <button
                  key={amount}
                  type="button"
                  onClick={() => handleChange('wartosc', amount.toString())}
                  style={{
                    padding: '4px 10px',
                    border: `1px solid ${parseFloat(formData.wartosc) === amount ? 'var(--info)' : 'var(--border)'}`,
                    color: parseFloat(formData.wartosc) === amount ? 'var(--info)' : 'var(--text-secondary)',
                    fontSize: '11px',
                    fontWeight: '500'
                  }}
                >
                  {amount} zł
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Sekcja danych kontaktowych */}
        <div style={{
          marginBottom: '0.75rem',
          padding: '0.75rem',
          background: 'var(--bg)',
          borderRadius: '5px',
          border: '1px solid var(--border)'
        }}>
          <h6 style={{ margin: '0 0 0.75rem 0', fontWeight: '600', color: 'var(--text-secondary)', fontSize: '12px' }}>
            📱 Dane kontaktowe (opcjonalne)
          </h6>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            {/* Numer telefonu */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: '500',
                color: 'var(--text-muted)',
                marginBottom: '3px'
              }}>
                Numer telefonu
              </label>
              <input
                type="tel"
                value={formData.numer_telefonu}
                onChange={(e) => handleChange('numer_telefonu', e.target.value)}
                placeholder="np. 500 123 456"
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  fontSize: '12px',
                  height: '32px'
                }}
              />
            </div>

            {/* Sposób płatności */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: '500',
                color: 'var(--text-muted)',
                marginBottom: '3px'
              }}>
                Sposób płatności
              </label>
              <select
                value={formData.sposob_platnosci}
                onChange={(e) => handleChange('sposob_platnosci', e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  fontSize: '12px',
                  height: '32px',
                  background: 'var(--card-bg)'
                }}
              >
                <option value="gotowka">💵 Gotówka</option>
                <option value="karta">💳 Karta</option>
                <option value="blik">📱 BLIK</option>
              </select>
            </div>
          </div>

          {/* Kod zewnętrzny */}
          <div style={{ marginTop: '0.75rem' }}>
            <label style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: '500',
              color: 'var(--text-muted)',
              marginBottom: '3px'
            }}>
              🔖 Kod zewnętrzny (opcjonalny)
            </label>
            <input
              type="text"
              value={formData.kod_zewnetrzny}
              onChange={(e) => handleChange('kod_zewnetrzny', e.target.value)}
              placeholder="np. kod kreskowy z fizycznej karty"
              style={{
                width: '100%',
                padding: '6px 8px',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                fontSize: '12px',
                height: '32px'
              }}
            />
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '3px' }}>
              Jeśli nie podasz, system wygeneruje własny kod automatycznie
            </div>
          </div>
        </div>

        {/* Sukces */}
        {success && (
          <div style={{
            color: 'var(--success)',
            background: 'var(--success-light)',
            border: '1px solid var(--success-light)',
            borderRadius: '4px',
            padding: '0.75rem',
            marginBottom: '0.75rem',
            fontSize: '12px',
            fontWeight: '500'
          }}>
            {success}
          </div>
        )}

        {/* Błąd */}
        {error && (
          <div style={{
            color: 'var(--danger)',
            background: 'var(--danger-light)',
            border: '1px solid var(--danger-light)',
            borderRadius: '4px',
            padding: '0.5rem',
            marginBottom: '0.75rem',
            fontSize: '11px'
          }}>
            ❌ {error}
          </div>
        )}

        {/* Podsumowanie */}
        {formData.wartosc && parseFloat(formData.wartosc) > 0 && (
          <div style={{
            padding: '0.75rem',
            background: 'var(--info-light)',
            borderRadius: '5px',
            marginBottom: '0.75rem',
            border: '1px solid var(--info-light)'
          }}>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>Podsumowanie:</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Wartość kuponu:</span>
              <span style={{ fontSize: '16px', fontWeight: '700', color: 'var(--info)' }}>
                {parseFloat(formData.wartosc || 0).toFixed(2)} zł
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Płatność:</span>
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                {formData.sposob_platnosci === 'gotowka' && '💵 Gotówka'}
                {formData.sposob_platnosci === 'karta' && '💳 Karta'}
                {formData.sposob_platnosci === 'blik' && '📱 BLIK'}
              </span>
            </div>
          </div>
        )}
      </form>
    </BaseModal>
  );
};

export default AddCouponModal;
